/*
  <social-blobs> — cursor-directional blob hover for the newsletter social
  circles. On mouseenter a circular blob grows out of the exact point the
  cursor crossed the edge; on mouseleave it shrinks back toward the exit point.
  Built on the Web Animations API (no deps). Skipped on touch / no-hover
  pointers and when the section opts out (data-animate="false").

  Wrap one or more button-like elements; each needs a `[data-blob]` child sized
  large enough to cover the element from any entry point (see the matching CSS).
*/
if (!customElements.get('social-blobs')) {
  customElements.define('social-blobs', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.animate === 'false') return;
      if (!window.matchMedia('(hover: hover)').matches) return;

      this.classList.add('social-blobs--ready');

      // Opt-in: also tell the custom cursor to suck into the button on hover.
      // This only drives the cursor — the blob effect below is untouched.
      const cursorSuck = this.dataset.cursorSuck !== undefined;

      this.querySelectorAll('[data-blob]').forEach((blob) => {
        const button = blob.parentElement;
        if (!button) return;
        button.addEventListener('mouseenter', (e) => {
          this.run(button, blob, e, true);
          if (cursorSuck) {
            const r = button.getBoundingClientRect();
            document.dispatchEvent(new CustomEvent('ink:absorb', {
              detail: { x: r.left + r.width / 2, y: r.top + r.height / 2 }
            }));
          }
        });
        button.addEventListener('mouseleave', (e) => {
          this.run(button, blob, e, false);
          if (cursorSuck) document.dispatchEvent(new CustomEvent('ink:release'));
        });
      });
    }

    run(button, blob, event, entering) {
      // Anchor the blob's centre at the cursor's position within the button.
      const rect = button.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;
      blob.style.left = `${px}px`;
      blob.style.top = `${py}px`;

      // Preserve the in-flight size so an interrupted hover continues
      // smoothly (single-keyframe animations start from the current value).
      if (blob._anim) {
        try { blob._anim.commitStyles(); } catch (e) { /* no-op */ }
        blob._anim.cancel();
      }
      const duration = parseInt(this.dataset.duration, 10) || 450;
      // In: smooth start that accelerates to a fast finish (ease-in).
      // Out: kept as a plain ease (fine as-is).
      const easing = entering ? 'cubic-bezier(0.5, 0, 1, 1)' : 'ease';

      // data-blob-mode="scale" — the ORIGINAL animation: a fixed-size blob
      // (sized in the section's CSS) scales 0 → 1 as a transform. Used by the
      // newsletter social circles, where the client prefers the old feel and
      // the compositor ring never read on the small icons.
      if (this.dataset.blobMode === 'scale') {
        const grown = 'translate(-50%, -50%) scale(1)';
        const seed = 'translate(-50%, -50%) scale(0)';
        blob._anim = blob.animate(
          entering ? [{ transform: seed }, { transform: grown }]
                   : [{ transform: grown }, { transform: seed }],
          { duration, easing, fill: 'forwards' }
        );
        return;
      }

      // Default: grows via width/height, NOT transform — an animated
      // transform is composited on its own layer and the rounded overflow
      // clip leaves a ~1px ring of the colour beneath at the curved edge.
      // Size changes are painted with the button itself, so the clip cuts
      // both identically.
      const far = Math.max(
        Math.hypot(px, py),
        Math.hypot(rect.width - px, py),
        Math.hypot(px, rect.height - py),
        Math.hypot(rect.width - px, rect.height - py)
      );
      const d = entering ? Math.ceil(far * 2 * 1.15) : 0;
      blob._anim = blob.animate(
        [{ width: `${d}px`, height: `${d}px` }],
        { duration, easing, fill: 'forwards' }
      );
    }
  });
}
